import { DownOutlined } from '@ant-design/icons';
import styles from './index.module.less';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { useConfigProvider } from '@/context/ConfigProvider';
import { ga_logEvent } from '@/shared/ga';
import HeaderDropdown from '@/components/Layouts/HeaderDropdown';
import type { StaticImageData } from 'next/image';

// 下拉菜单布局类型
export type DropdownLayout = 'simple' | 'columns' | 'tabs';

// 图标类型：支持 URL 字符串或 SVG 文件导入
export type NavIconType = string | StaticImageData;

// 菜单项基础接口
export interface NavMenuItem {
  text: string;
  path?: string;
  gaName?: string;
  icon?: NavIconType;
  description?: string;
  isExternal?: boolean; // 是否外部链接
  children?: NavMenuItem[]; // 子菜单项
}

// 菜单分组（用于列布局）
export interface NavMenuGroup {
  title: string;
  titleIcon?: NavIconType;
  items: NavMenuItem[];
}

// 标签页分类（用于 Tools 菜单的 For Creator/For Individuals/General）
export interface NavMenuTab {
  key: string;
  label: string;
  icon?: NavIconType;
  sections: NavMenuSection[]; // 该 tab 下的内容区块
}

// 内容区块（如 Shadowban Test、Reverse Search）
export interface NavMenuSection {
  title: string;
  titleIcon?: NavIconType;
  path?: string; // 标题可点击跳转
  items: NavMenuItem[];
}

// 下拉菜单配置
export interface DropdownConfig {
  layout: DropdownLayout;
  // simple 布局：简单列表
  groups?: NavMenuGroup[];
  // tabs 布局：标签页切换
  tabs?: NavMenuTab[];
  // 右侧固定区域（如 Platform Guides）
  sidePanel?: NavMenuSection;
}

// 主导航项接口
export interface MarketplaceNavItem {
  text: string;
  path?: string;
  gaName?: string;
  dropdownConfig?: DropdownConfig;
  // eslint-disable-next-line no-unused-vars
  onClick?: (item: MarketplaceNavItem) => void;
}

const NavItem = ({ item, className }: { item: MarketplaceNavItem; className?: string }) => {
  const { isMobile } = useConfigProvider();
  const router = useRouter();

  const hasDropdown = !!item.dropdownConfig;

  // 检查路径是否匹配（精确匹配或作为路径前缀匹配）
  const isPathMatch = (menuPath: string, currentPath: string): boolean => {
    if (currentPath === menuPath) return true;
    // 确保是路径前缀匹配，而不是字符串子串匹配
    // 例如：/dmca-takedown 不应该匹配 /dmca-takedown-service
    return currentPath.startsWith(menuPath + '/');
  };

  // 检查当前路径是否匹配菜单项
  const checkIsActive = (): boolean => {
    if (router.pathname === item.path) return true;
    if (!item.dropdownConfig) return false;

    const { groups, tabs } = item.dropdownConfig;

    // 检查 groups 中的路径
    if (groups) {
      for (const group of groups) {
        if (group.items.some((i) => i.path && isPathMatch(i.path, router.pathname))) {
          return true;
        }
      }
    }

    // 检查 tabs 中的路径
    if (tabs) {
      for (const tab of tabs) {
        for (const section of tab.sections) {
          if (section.path && isPathMatch(section.path, router.pathname)) return true;
          if (section.items.some((i) => i.path && isPathMatch(i.path, router.pathname))) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const isActive = checkIsActive();

  return (
    <div
      className={classNames(styles.navItemWarp, className, {
        [styles.active]: isActive,
      })}
      onClick={(e) => {
        if (!isMobile) {
          e.stopPropagation();
        }
        if (item.gaName) {
          ga_logEvent(item?.gaName);
        }
        if (item.path) {
          router.push(item.path);
        }
      }}
    >
      <div className={styles.navItemText}>{item.text}</div>
      {hasDropdown && (
        <DownOutlined
          style={{
            fontSize: '12px',
            marginLeft: '5px',
          }}
        />
      )}
      {hasDropdown && (
        <div className={styles.navDropdown}>
          <div className={styles.navDropdownContent}>
            <HeaderDropdown config={item.dropdownConfig} />
          </div>
        </div>
      )}
    </div>
  );
};
export default NavItem;
